import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { Button, ProgressBar } from '../../components/ui';
import { motion } from 'framer-motion';

// Radar chart component for visualizing progress
function RadarChart({ values, size = 160, animate = true }: {
  values: number[];
  size?: number;
  animate?: boolean;
}) {
  const labels = ['Skin', 'Jawline', 'Symmetry', 'Eyes', 'Overall'];
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.4;
  const angles = labels.map((_, i) => (i * 2 * Math.PI) / labels.length - Math.PI / 2);

  // Calculate points for the polygon
  const points = values.map((value, i) => {
    const r = (value / 100) * radius;
    const x = centerX + r * Math.cos(angles[i]);
    const y = centerY + r * Math.sin(angles[i]);
    return `${x},${y}`;
  }).join(' ');

  // Calculate label positions
  const labelPositions = labels.map((_, i) => {
    const r = radius + 20;
    const x = centerX + r * Math.cos(angles[i]);
    const y = centerY + r * Math.sin(angles[i]);
    return { x, y };
  });

  return (
    <svg width={size} height={size} className="mx-auto">
      {/* Background grid */}
      {[0.25, 0.5, 0.75, 1].map((scale) => (
        <polygon
          key={scale}
          points={angles.map((angle) => {
            const x = centerX + radius * scale * Math.cos(angle);
            const y = centerY + radius * scale * Math.sin(angle);
            return `${x},${y}`;
          }).join(' ')}
          fill="none"
          stroke="var(--color-border)"
          strokeWidth="1"
        />
      ))}

      {/* Axis lines */}
      {angles.map((angle, i) => (
        <line
          key={i}
          x1={centerX}
          y1={centerY}
          x2={centerX + radius * Math.cos(angle)}
          y2={centerY + radius * Math.sin(angle)}
          stroke="var(--color-border)"
          strokeWidth="1"
        />
      ))}

      {/* Data polygon */}
      <motion.polygon
        points={points}
        fill="var(--color-accent)"
        fillOpacity="0.3"
        stroke="var(--color-accent)"
        strokeWidth="2"
        initial={animate ? { opacity: 0, scale: 0.5 } : false}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        style={{ transformOrigin: `${centerX}px ${centerY}px` }}
      />

      {/* Data points */}
      {values.map((value, i) => {
        const r = (value / 100) * radius;
        const x = centerX + r * Math.cos(angles[i]);
        const y = centerY + r * Math.sin(angles[i]);
        return (
          <motion.circle
            key={i}
            cx={x}
            cy={y}
            r="4"
            fill="var(--color-accent)"
            initial={animate ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 + i * 0.1 }}
          />
        );
      })}

      {/* Labels */}
      {labels.map((label, i) => (
        <text
          key={label}
          x={labelPositions[i].x}
          y={labelPositions[i].y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="var(--color-text-secondary)"
          fontSize="10"
        >
          {label}
        </text>
      ))}
    </svg>
  );
}

export function Week1Screen() {
  const navigate = useNavigate();
  const { nextStep } = useStore();
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowText(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleContinue = () => {
    nextStep();
    navigate('/onboarding/week10');
  };

  // Low baseline values for Week 1
  const week1Values = [25, 20, 30, 25, 20];

  return (
    <div className="screen-container onboarding-screen">
      <ProgressBar current={12} total={17} className="mb-8" />

      <div className="flex-1 flex flex-col">
        <h1 className="text-3xl font-bold mb-8">Your first week</h1>

        <div className="flex-1 flex flex-col items-center justify-center">
          <RadarChart values={week1Values} size={200} />

          <motion.div
            className="mt-8 text-center space-y-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: showText ? 1 : 0, y: showText ? 0 : 20 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-3xl md:text-4xl font-semibold">Early changes start fast.</p>
            <p className="text-3xl md:text-4xl font-semibold text-accent">Momentum builds.</p>
          </motion.div>
        </div>
      </div>

      <Button onClick={handleContinue}>
        Continue
      </Button>
    </div>
  );
}

export { RadarChart };
